import { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Checkbox,
  Button,
  Group,
  Divider,
  Badge,
  Alert,
  LoadingOverlay,
} from "@mantine/core";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  useFormTemplateAssignments,
  useAssignFormTemplate,
  useUnassignFormTemplate,
} from "../../hooks/useFormTemplates";
import { useFacilityOptions } from "../../hooks/useFacilities";
import { useGroupHomes } from "../../hooks/useGroupHomes";
import { useAuth, useIsPlatformUser } from "../../hooks/useAuth";

interface FormAssignmentModalProps {
  opened: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
}

export function FormAssignmentModal({
  opened,
  onClose,
  templateId,
  templateName,
}: FormAssignmentModalProps) {
  const { data: context } = useAuth();
  // Platform admins and managers have no facility/group-home scope — show everything
  const isPlatformUser = useIsPlatformUser();
  const { data: assignments, isLoading: loadingAssignments } =
    useFormTemplateAssignments(templateId);
  // Bug fix: this modal must work for platform admins assigning a GLOBAL
  // template to facilities — useFacilities() (portal-scoped /facilities)
  // 403s for them, leaving `facilities` permanently undefined even though
  // the isPlatformUser branch below expects to show the full list.
  // useFacilityOptions() routes platform users to /admin/facilities instead.
  const { data: facilities } = useFacilityOptions();
  // GET /admin/group-homes requires the platform-tier group-home:read
  // permission — org admins/facility managers 403 on it. There's no
  // non-admin group-homes list endpoint to fall back to (unlike facilities),
  // so the Group Homes section is platform-only; non-platform users assign
  // at the facility level only. Query stays disabled for them so it never
  // fires (and never 403s in the console).
  const { data: groupHomes } = useGroupHomes({ enabled: isPlatformUser });
  const assignMutation = useAssignFormTemplate(templateId);
  const unassignMutation = useUnassignFormTemplate(templateId);

  // Derive the server-persisted selection directly from query data — no local
  // state needed for what's already saved. We only track the user's unsaved
  // additions and removals relative to the server baseline.
  const serverGroupHomeIds = useMemo(
    () =>
      assignments?.group_homes?.map(
        (a: { group_home_id: string }) => a.group_home_id,
      ) ?? [],
    [assignments],
  );
  const serverFacilityIds = useMemo(
    () =>
      assignments?.facilities?.map(
        (a: { facility_id: string }) => a.facility_id,
      ) ?? [],
    [assignments],
  );

  // Delta tracking: sets of IDs the user has added or removed in this session
  const [addedGroupHomeIds, setAddedGroupHomeIds] = useState<string[]>([]);
  const [removedGroupHomeIds, setRemovedGroupHomeIds] = useState<string[]>([]);
  const [addedFacilityIds, setAddedFacilityIds] = useState<string[]>([]);
  const [removedFacilityIds, setRemovedFacilityIds] = useState<string[]>([]);

  // Reset delta when modal closes so stale selections don't bleed into the next open
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!opened) {
      setAddedGroupHomeIds([]);
      setRemovedGroupHomeIds([]);
      setAddedFacilityIds([]);
      setRemovedFacilityIds([]);
    }
  }, [opened]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Derive current visible selection from server data + local delta
  const selectedGroupHomeIds = useMemo(
    () => [
      ...serverGroupHomeIds.filter(
        (id: string) => !removedGroupHomeIds.includes(id),
      ),
      ...addedGroupHomeIds,
    ],
    [serverGroupHomeIds, addedGroupHomeIds, removedGroupHomeIds],
  );
  const selectedFacilityIds = useMemo(
    () => [
      ...serverFacilityIds.filter(
        (id: string) => !removedFacilityIds.includes(id),
      ),
      ...addedFacilityIds,
    ],
    [serverFacilityIds, addedFacilityIds, removedFacilityIds],
  );

  // Aliases used by handleSave to compute mutations
  const initialGroupHomeIds = serverGroupHomeIds;
  const initialFacilityIds = serverFacilityIds;

  // groupHomes is only ever fetched for platform users (see useGroupHomes()
  // call above) — the section itself is gated on isPlatformUser below, so no
  // non-platform filter branch is needed here.
  const accessibleGroupHomes = groupHomes;

  const accessibleFacilities = isPlatformUser
    ? facilities
    : facilities?.filter((f) => context?.facilityIds.includes(f.id));

  const handleSave = async () => {
    try {
      // Determine what to add
      const groupHomesToAdd = selectedGroupHomeIds.filter(
        (id: string) => !initialGroupHomeIds.includes(id),
      );
      const facilitiesToAdd = selectedFacilityIds.filter(
        (id: string) => !initialFacilityIds.includes(id),
      );

      // Determine what to remove
      const groupHomesToRemove = initialGroupHomeIds.filter(
        (id: string) => !selectedGroupHomeIds.includes(id),
      );
      const facilitiesToRemove = initialFacilityIds.filter(
        (id: string) => !selectedFacilityIds.includes(id),
      );

      // Add new assignments
      if (groupHomesToAdd.length > 0 || facilitiesToAdd.length > 0) {
        await assignMutation.mutateAsync({
          group_home_ids:
            groupHomesToAdd.length > 0 ? groupHomesToAdd : undefined,
          facility_ids:
            facilitiesToAdd.length > 0 ? facilitiesToAdd : undefined,
        });
      }

      // Remove old assignments
      for (const ghId of groupHomesToRemove) {
        await unassignMutation.mutateAsync({ group_home_id: ghId });
      }
      for (const fId of facilitiesToRemove) {
        await unassignMutation.mutateAsync({ facility_id: fId });
      }

      notifications.show({
        title: "Success",
        message: "Form assignments updated successfully",
        color: "green",
        icon: <IconCheck size={16} />,
      });

      onClose();
    } catch {
      /* error toast comes from the global mutation handler (US-081) */
    }
  };

  const hasChanges =
    JSON.stringify(selectedGroupHomeIds.sort()) !==
      JSON.stringify(initialGroupHomeIds.sort()) ||
    JSON.stringify(selectedFacilityIds.sort()) !==
      JSON.stringify(initialFacilityIds.sort());

  const isLoading = assignMutation.isPending || unassignMutation.isPending;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Assign Form: ${templateName}`}
      size="lg"
    >
      <LoadingOverlay visible={loadingAssignments || isLoading} />

      <Stack gap="lg">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="blue"
          variant="light"
        >
          <Text size="sm">
            Assign this form to group homes and/or facilities to make it
            available for staff to use. By default, forms are visible to
            everyone but can only be filled out in assigned locations.
          </Text>
        </Alert>

        {/* Group Homes Section — platform-only (see useGroupHomes() call above) */}
        {isPlatformUser && (
          <>
            <div>
              <Group justify="space-between" mb="xs">
                <Text fw={500} size="sm">
                  Group Homes
                </Text>
                <Badge color="gray" size="sm">
                  {selectedGroupHomeIds.length} selected
                </Badge>
              </Group>

              {accessibleGroupHomes && accessibleGroupHomes.length > 0 ? (
                <Stack gap="xs">
                  {accessibleGroupHomes.map((gh) => (
                    <Checkbox
                      key={gh.id}
                      label={gh.name}
                      checked={selectedGroupHomeIds.includes(gh.id)}
                      onChange={(event) => {
                        if (event.currentTarget.checked) {
                          setAddedGroupHomeIds((prev) => [...prev, gh.id]);
                          setRemovedGroupHomeIds((prev) =>
                            prev.filter((id) => id !== gh.id),
                          );
                        } else {
                          setRemovedGroupHomeIds((prev) => [...prev, gh.id]);
                          setAddedGroupHomeIds((prev) =>
                            prev.filter((id) => id !== gh.id),
                          );
                        }
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">
                  No group homes available
                </Text>
              )}
            </div>

            <Divider />
          </>
        )}

        {/* Facilities Section */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text fw={500} size="sm">
              Facilities
            </Text>
            <Badge color="gray" size="sm">
              {selectedFacilityIds.length} selected
            </Badge>
          </Group>

          {accessibleFacilities && accessibleFacilities.length > 0 ? (
            <Stack gap="xs">
              {accessibleFacilities.map((facility) => (
                <Checkbox
                  key={facility.id}
                  label={facility.name}
                  checked={selectedFacilityIds.includes(facility.id)}
                  onChange={(event) => {
                    if (event.currentTarget.checked) {
                      setAddedFacilityIds((prev) => [...prev, facility.id]);
                      setRemovedFacilityIds((prev) =>
                        prev.filter((id) => id !== facility.id),
                      );
                    } else {
                      setRemovedFacilityIds((prev) => [...prev, facility.id]);
                      setAddedFacilityIds((prev) =>
                        prev.filter((id) => id !== facility.id),
                      );
                    }
                  }}
                />
              ))}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              No facilities available
            </Text>
          )}
        </div>

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges}
            loading={isLoading}
          >
            Save Assignments
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
